import { LAND_USE_CATEGORIES } from './land-use-definitions';
import { EDUCATION_GROUPS, EDUCATION_GROUP_COLORS, EDUCATION_GROUP_LABELS } from '../layers/EducationFacilitiesLayer';
import { BUILDING_LINE_STYLES, MPZP_DESIGNATION_GROUPS, buildingLineLegendShape, rgbHex } from '../layers/MpzpImportedLayer';

export const OWNERSHIP_COLORS = [
    '#f07878', '#c63f3f', '#9f3f3f', '#a3e0e0',
    '#60bbff', '#3f7bb7', '#ffe0c0', '#ffc0c0',
    '#a349a3', '#e0a3e0', '#3fe43f', '#3f9f3f',
    '#ffd950', '#d5b03f', '#f5cdaa', '#a0a0a0',
    '#dcdcdc',
];

export function ownershipColor(groupCode) {
    const code = Number(groupCode);
    return Number.isInteger(code) && code >= 1 && code <= 16
        ? OWNERSHIP_COLORS[code - 1]
        : OWNERSHIP_COLORS[16];
}

export const MAP_LEGEND_DEFINITIONS = {
    ortho: {
        title: 'Ortofotomapa',
        items: [{ label: 'Zdjęcie lotnicze', shape: 'image' }],
        note: 'Przybliż mapę, aby zobaczyć więcej szczegółów terenu.',
    },
    mpzp: {
        title: 'Miejscowy plan zagospodarowania przestrzennego',
        items: [{ label: 'Rysunek planu', shape: 'multicolor' }],
        note: 'Znaczenie kolorów i symboli wynika z legendy konkretnego planu.',
    },
    mpzpRu: {
        title: 'Przeznaczenia w miejscowych planach',
        items: [
            ...MPZP_DESIGNATION_GROUPS.map((group) => ({ label: group.label, color: rgbHex(group.rgb) })),
            ...BUILDING_LINE_STYLES.map((style) => ({ label: style.label, color: style.color, shape: buildingLineLegendShape(style) })),
        ],
        note: 'Kliknij kolorowy obszar, aby poznać jego symbol i przeznaczenie. Linie zabudowy widać po przybliżeniu mapy, gdy gmina publikuje je w swoim serwisie planów.',
    },
    pog: {
        title: 'Plan ogólny gminy',
        items: [{ label: 'Strefy planistyczne', shape: 'multicolor' }],
        note: 'Kliknij obszar, aby zobaczyć dostępne szczegóły planu.',
    },
    studium: {
        title: 'Studium uwarunkowań i kierunków',
        items: [{ label: 'Rysunek studium', shape: 'multicolor' }],
        note: 'Znaczenie kolorów i symboli zależy od dokumentu gminy.',
    },
    powodz: {
        title: 'Prawdopodobieństwo powodzi w dowolnym roku',
        items: [
            { label: '10% — wysokie', color: '#1e40af' },
            { label: '1% — średnie', color: '#3b82f6' },
            { label: '0,2% — niskie', color: '#60a5fa' },
        ],
        note: 'Odcień może się różnić wraz z modelowaną głębokością wody.',
    },
    portPolska: {
        title: 'Rezerwacje terenu Port Polska',
        singleColumn: true,
        // Colours read from the service GetLegendGraphic (2026-10-07); keep in sync with PortPolskaLayer.
        items: [
            { label: 'Plan rezerwacji terenu', color: '#faa280' },
            { label: 'Zakaz inwestycyjny', color: '#71d1e1' },
            { label: 'Zakaz inwestycyjny i prawo pierwokupu', color: '#a9c179' },
            { label: 'Obszar prawa wykonywania badań', color: '#7a5aa6', shape: 'dashed-line' },
        ],
        note: 'Zasięgi z usługi Geoportalu, sprawdzane codziennie. Skutki prawne opisuje zarządzenie wojewody lub ustawa o Centralnym Porcie Komunikacyjnym.',
    },
    gesut: {
        title: 'Sieci uzbrojenia terenu',
        items: [{ label: 'Przewód lub urządzenie sieci', shape: 'multicolor-line' }],
        note: 'Kolory rozróżniają rodzaje sieci zgodnie z oznaczeniami Geoportalu.',
    },
    sidusis: {
        title: 'Dostępność internetu stacjonarnego',
        singleColumn: true,
        // Official internet.gov.pl legend, checked against public GetStyles.
        items: [
            { label: 'W zasięgu', color: '#2479FF', shape: 'solid' },
            { label: 'Zasięg hurtowy', color: '#3CD7FF', shape: 'solid' },
            { label: 'Zasięg teoretyczny', color: '#D446F5', shape: 'solid' },
            { label: 'Inwestycja planowana ze środków prywatnych', color: '#138708', shape: 'solid' },
            { label: 'Inwestycja planowana ze środków publicznych', color: '#A0CF3F', shape: 'solid' },
            { label: 'Inwestycja dofinansowana — brak realizacji', color: '#FFF59D', shape: 'solid' },
            { label: 'Brak zasięgu', color: '#FFFFFF', shape: 'solid' },
            { label: 'Granice województw', color: '#3579b1', shape: 'solid-line' },
            { label: 'Granice powiatów', color: '#d066e9', shape: 'solid-line' },
            { label: 'Granice gmin', color: '#F3605E', shape: 'solid-line' },
            { label: 'Granice obrębów', color: '#e6a05b', shape: 'solid-line' },
        ],
        note: 'Kolory przedstawiają deklarowaną dostępność i plany inwestycji operatorów; uruchomienie usługi pod adresem potwierdza operator. Szczegóły adresu sprawdzisz w serwisie źródłowym.',
        source: {
            label: 'Sprawdź adres i szczegóły na internet.gov.pl',
            href: 'https://internet.gov.pl/',
        },
    },
    education: {
        title: 'Szkoły, przedszkola i żłobki',
        singleColumn: true,
        // Groups as active points, then the three other registry states drawn in
        // the colour of primary schools (EducationFacilitiesLayer.pointPaint()).
        items: EDUCATION_GROUPS.map(group => ({
            label: EDUCATION_GROUP_LABELS[group],
            color: 'var(--nm-layer-education-' + group + ', ' + EDUCATION_GROUP_COLORS[group] + ')',
            shape: 'solid-dot',
        })).concat([
            { label: 'Planowana — punkt z pustym środkiem', shape: 'hollow-dot' },
            { label: 'Niepotwierdzona w ostatniej aktualizacji rejestru — jasne wypełnienie', shape: 'faded-dot' },
            { label: 'Zawieszona przez prowadzącego — ciemna obwódka', shape: 'suspended-dot' },
        ].map(item => Object.assign(item, {
            color: 'var(--nm-layer-education-primary, ' + EDUCATION_GROUP_COLORS.primary + ')',
        }))),
        note: 'Kolor punktu oznacza grupę placówki. Pusty środek, jasne wypełnienie i ciemna obwódka oznaczają stan w rejestrze. Kliknij punkt, aby zobaczyć nazwę, rodzaj, adres i stan placówki.',
    },
    kowr: {
        title: 'Zasób Krajowego Ośrodka Wsparcia Rolnictwa',
        items: [{ label: 'Działka w zasobie', color: '#15803d' }],
        note: 'Kliknij zielony obszar, aby zobaczyć informacje o działce.',
    },
    ownership: {
        title: 'Mapa własności — grupy rejestrowe Ewidencji Gruntów i Budynków',
        items: [
            { label: 'Skarb Państwa — bez użytkowania wieczystego', color: OWNERSHIP_COLORS[0] },
            { label: 'Skarb Państwa — z użytkowaniem wieczystym', color: OWNERSHIP_COLORS[1] },
            { label: 'Państwowe osoby prawne i przedsiębiorstwa', color: OWNERSHIP_COLORS[2] },
            { label: 'Gminy i związki — bez użytkowania wieczystego', color: OWNERSHIP_COLORS[3] },
            { label: 'Gminy i związki — z użytkowaniem wieczystym', color: OWNERSHIP_COLORS[4] },
            { label: 'Spółki i osoby prawne samorządu', color: OWNERSHIP_COLORS[5] },
            { label: 'Osoby fizyczne', color: OWNERSHIP_COLORS[6] },
            { label: 'Spółdzielnie', color: OWNERSHIP_COLORS[7] },
            { label: 'Kościoły i związki wyznaniowe', color: OWNERSHIP_COLORS[8] },
            { label: 'Wspólnoty gruntowe', color: OWNERSHIP_COLORS[9] },
            { label: 'Powiaty i związki — bez użytkowania wieczystego', color: OWNERSHIP_COLORS[10] },
            { label: 'Powiaty i związki — z użytkowaniem wieczystym', color: OWNERSHIP_COLORS[11] },
            { label: 'Województwa — bez użytkowania wieczystego', color: OWNERSHIP_COLORS[12] },
            { label: 'Województwa — z użytkowaniem wieczystym', color: OWNERSHIP_COLORS[13] },
            { label: 'Spółki prawa handlowego', color: OWNERSHIP_COLORS[14] },
            { label: 'Inne podmioty ewidencyjne', color: OWNERSHIP_COLORS[15] },
            { label: 'Brak danych', color: OWNERSHIP_COLORS[16] },
        ],
        note: 'Kategoria opisuje typ właściciela lub władającego; nie identyfikuje konkretnej osoby ani firmy i nie potwierdza tytułu prawnego. Brak danych nie oznacza własności prywatnej ani publicznej.',
    },
    landUse: {
        title: 'Użytki gruntowe według Ewidencji Gruntów i Budynków',
        items: [
            { label: LAND_USE_CATEGORIES.agricultural.label, color: LAND_USE_CATEGORIES.agricultural.color },
            { label: LAND_USE_CATEGORIES.forest.label, color: LAND_USE_CATEGORIES.forest.color },
            { label: LAND_USE_CATEGORIES.builtUp.label, color: LAND_USE_CATEGORIES.builtUp.color },
            { label: LAND_USE_CATEGORIES.transport.label, color: LAND_USE_CATEGORIES.transport.color },
            { label: LAND_USE_CATEGORIES.water.label, color: LAND_USE_CATEGORIES.water.color },
            { label: LAND_USE_CATEGORIES.other.label, color: LAND_USE_CATEGORIES.other.color },
            { label: LAND_USE_CATEGORIES.mixed.label, color: LAND_USE_CATEGORIES.mixed.color },
            { label: LAND_USE_CATEGORIES.noData.label, color: LAND_USE_CATEGORIES.noData.color },
        ],
        note: 'Kolor to własna legenda Metroskopu, która grupuje kody użytków zapisane w Ewidencji Gruntów i Budynków. Gdy działka ma kilka kodów bez informacji o powierzchni ich części, pokazujemy ją jako użytki mieszane zamiast wskazywać użytek dominujący.',
    },
    dzialki: {
        title: 'Działki ewidencyjne',
        items: [{ label: 'Granica i numer działki', color: '#3f6212', shape: 'line' }],
        note: 'Kliknij działkę, aby otworzyć jej dane i powiązane obiekty.',
    },
    gunb: {
        title: 'Pozwolenia na budowę i zgłoszenia',
        items: [{ label: 'Inwestycja w rejestrze', color: '#7c3aed', shape: 'dot' }],
        note: 'Kliknij punkt lub obszar, aby poznać szczegóły postępowania.',
    },
    transakcje: {
        title: 'Transakcje',
        items: [
            { label: 'Działka lub budynek z obrysem', color: 'var(--nm-layer-transactions, #1e3a8a)' },
            { label: 'Lokal lub obiekt bez obrysu', color: 'var(--nm-layer-transactions, #1e3a8a)', shape: 'dot' },
        ],
        note: 'Kliknij punkt, obszar lub grupę, aby zobaczyć ceny i szczegóły.',
    },
    sygnaly: {
        title: 'Sygnały społeczności',
        items: [
            { label: 'Sygnał pozytywny', color: '#10d758', shape: 'dot' },
            { label: 'Sygnał negatywny', color: '#e15759', shape: 'dot' },
            { label: 'Sygnał neutralny', color: '#677686', shape: 'dot' },
        ],
        note: 'Kliknij znacznik, aby przeczytać zgłoszenie użytkownika.',
    },
    ogloszenia: {
        title: 'Ogłoszenia nieruchomości',
        items: [
            { label: 'Sprzedaż', color: 'var(--nm-layer-listings, #2563eb)', shape: 'dot' },
            { label: 'Wynajem', color: 'var(--nm-layer-listings-rent, #0e7490)', shape: 'dot' },
        ],
        note: 'Kliknij znacznik lub grupę, aby przejść do ogłoszeń.',
    },
    ofertyDeweloperow: {
        title: 'Oferty deweloperów',
        items: [{ label: 'Inwestycja i ceny ofertowe', color: '#b73e1d', shape: 'dot' }],
        note: 'Kliknij znacznik, aby zobaczyć lokale dostępne w inwestycji.',
    },
    budynki: {
        title: 'Budynki ewidencyjne',
        items: [{ label: 'Obrys i bryła budynku', color: '#e7e1d7' }],
        note: 'Kliknij budynek, aby zobaczyć jego dane i powiązania.',
    },
    otoczenie: {
        title: 'Otoczenie',
        items: [
            { label: 'Obiekt użyteczny', color: '#15803d' },
            { label: 'Obiekt uciążliwy', color: '#dc2626' },
            { label: 'Pozostały obiekt', color: '#0f766e' },
            { label: 'Korytarz lotniczy', color: '#7c3aed', shape: 'line' },
            { label: 'Bufor linii energetycznej', color: '#f59e0b', shape: 'line' },
        ],
        note: 'Kliknij obiekt, aby sprawdzić jego rodzaj i ocenę wpływu.',
    },
};
